import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../../_generated';
import { RouterService } from '../../shared/services/router.service';
import { getTeammateVisualFromParts } from '../../teammates/teammate-avatar.config';
import { TeammateVisualComponent } from '../../teammates/teammate-visual.component';

@Component({
  selector: 'squadbox-navigation-teammate',
  imports: [
    TranslatePipe,
    fromSeriousSystem.UseAiIconDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    TeammateVisualComponent,
  ],
  template: `
    @let navigationSlug = slug();
    @switch (navigationSlug) {
      @case ('my-skills') {
        <button [class.active]="active()" (click)="handleClickLibraryCTA()">
          <div class="avatar">
            <svg sdUseNavigationIcon="library"></svg>
          </div>
          {{ 'MAIN_NAVIGATION_ITEMS.MY_SKILLS' | translate }}
        </button>
      }
      @default {
        @let navigationTeammate = teammate();
        @if (navigationTeammate) {
          <button
            [class.active]="active()"
            (click)="handleClickTeammateCTA(navigationSlug)"
            (pointerenter)="teammateAvatarHovered.set(true)"
            (pointerleave)="teammateAvatarHovered.set(false)"
          >
            <div class="avatar teammate-avatar">
              <!-- The avatar arm paints 100ms late so the expanded nav panel's
                   fade-in animation can run; the avatar's heavy init (GSAP, SVG)
                   would otherwise block the first paint and prevent the panel
                   opacity transition from animating. The icon arm has no GSAP,
                   so the seam paints it immediately rather than holding it
                   behind a placeholder for nothing. -->
              <squadbox-teammate-visual
                class="h-full w-full"
                [visual]="teammateVisual()!"
                presentation="nav-slot"
                [active]="active()"
                [hovered]="teammateAvatarHovered()"
                [avatarDelayMs]="100"
                avatarClass="h-full w-full fade-in"
                placeholderClass="h-full w-full min-h-12 min-w-12 bg-primary-50 rounded-lg"
              />
            </div>
            <div class="info">
              <span class="name">{{ navigationTeammate.name }}</span>
              <span class="description">{{ navigationTeammate.title }}</span>
            </div>
          </button>
        }
      }
    }
  `,
  styles: [
    `
      @reference '../../../../styles.css';

      button {
        @apply flex items-center gap-3 w-full
          typo-p2 tablet-portrait:typo-p3 font-semibold
          py-2 px-3
          hover:bg-primary-50 rounded;

        &:hover > .avatar:not(.teammate-avatar) {
          @apply tablet-portrait:bg-primary-100;
        }

        &.active {
          @apply bg-primary-50;

          & > .avatar:not(.teammate-avatar) {
            @apply bg-primary-100;
          }
        }

        .avatar {
          @apply relative bg-primary-50 rounded-lg size-12
            p-4 tablet-portrait:p-3
            text-primary-300
            overflow-hidden;

          &.teammate-avatar {
            @apply p-0 bg-transparent text-inherit;
          }

          svg,
          img {
            @apply shrink-0 max-w-20 text-primary-300;
          }
        }

        .info {
          @apply flex flex-col text-left;

          .name {
            @apply typo-p2 tablet-portrait:typo-p3;
          }

          .description {
            @apply typo-p3 tablet-portrait:typo-caption text-neutral-500 font-normal;
          }
        }
      }
    `,
  ],
})
export class NavigationTeammateComponent {
  private readonly routerService = inject(RouterService);

  public readonly slug = input.required<string>();
  /**
   * SB-3519: `visualType` and `icon` are part of this Pick, and leaving them
   * out is a silent trap — it compiles, and every teammate then renders as an
   * avatar forever, because `visualType` simply arrives undefined and the
   * resolver falls to the avatar arm. The parent passes a whole `TeammateView`,
   * so widening the Pick is all that is needed.
   */
  public readonly teammate =
    input<
      Pick<
        fromGenerated.TeammateView,
        'name' | 'title' | 'color' | 'shape' | 'eyes' | 'visualType' | 'icon'
      >
    >();
  public readonly active = input<boolean>(false);
  public readonly teammateAvatarHovered = signal(false);
  /**
   * Memoized, and this is the third site where that is load-bearing rather
   * than tidy: like the Landing card and the Teammate page, a fresh object per
   * change-detection pass re-dirties the avatar's init effect and kills an
   * in-flight hover animation — and this row binds `hovered`.
   */
  public readonly teammateVisual = computed(() => {
    const teammate = this.teammate();
    return teammate ? getTeammateVisualFromParts(teammate) : undefined;
  });

  public async handleClickLibraryCTA() {
    await this.routerService.navigateToAssistants('createdByMe');
  }

  public async handleClickTeammateCTA(slug: string) {
    await this.routerService.navigateToTeammate(slug);
  }
}
